import React, {useState} from "react";
import {Column, ColumnGrid} from "../../../ui/spectre/grid";
import {PlayerLink, VillageLink} from "../../ingameLinks";
import {Countdown, Time} from "../../../ui/timers";
import Editable from "../../../ui/editable";
import {Modal} from "../../../ui/spectre/modal";
import translate from "../../../../../translate";
import Sizes from "../../../ui/spectre/common/sizes";
import {SpeedAnalysis} from "../../calculators/speedanalysis";
import CoalesceFunction from "../../../../../helper/coalesceFunction";
import {Button, ButtonTypes, LinkButton} from "../../../ui/spectre/buttons";
import {Icon} from "../../../ui/spectre/icon";
import WithTooltip from "../../../ui/withTooltip";
import {Dropdown, DropdownElement} from "../../../ui/spectre/dropdown";
import BTIncomingAttackClassification from "../../../../../enums/BTIncomingAttackClassification";

/**
 *
 * @param data {{ id, attackerSid, attackerDid, defenderSid, defenderDid, arrivalTime, baseSpeed, tournamentSquare,
 *                  bootsBonus, artefactBonus, standardBonus, comment, lastUnseen, firstSeen, classification }}
 * @param onChange {function(newParameters: { comment, firstSeen, lastUnseen, baseSpeed, tournamentSquare, artefactBonus, bootsBonus, standardBonus }, id: int): void}
 * @param groupId {int|null} ID of the group for this attack is displayed. Used for integrated links.
 * @returns {JSX.Element}
 */
export default function IncomingAttack({ data, groupId, onChange }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [analysisParameters, setAnalysisParameters] = useState(null);

    onChange = CoalesceFunction(onChange);

    return <>
        {
            !modalOpen
                ? null
                : <Modal
                    size={Sizes.lg}
                    title={translate('minitool/trav/speed_analysis/title')}
                    onClose={() => setModalOpen(false)}
                    buttons={[ { value: 'save', content: translate('dialog_save') } ]}
                    onButtonClicked={() => analysisParameters === null ? null : onChange(analysisParameters, data.id)}
                >
                    <SpeedAnalysis
                        onChange={setAnalysisParameters}
                        start={data.attackerDid}
                        target={data.defenderDid}
                        lastUnseen={data.lastUnseen}
                        firstSeen={data.firstSeen}
                        arrival={data.arrivalTime}
                        artefactBonus={data.artefactBonus}
                        bootsBonus={data.bootsBonus}
                        standardBonus={data.standardBonus}
                    />
                </Modal>
        }
        <ColumnGrid>
            <Column sizes={[1, null, 2]}><PlayerLink uid={data.attackerSid} /></Column>
            <Column sizes={[1, null, 2]}><VillageLink vid={data.attackerDid} /></Column>
            <Column sizes={[1, null, 2]}><PlayerLink uid={data.defenderSid} /></Column>
            <Column sizes={[1, null, 2]}><VillageLink vid={data.defenderDid} /></Column>
            <Column sizes={[1, null, 2]}><Countdown endTime={data.arrivalTime} /></Column>
            <Column sizes={[1, null, 2]}><Time time={data.arrivalTime} /></Column>
            <Column sizes={[2, null, 4]}>
                <div className={'d-flex pr-2'} style={{maxWidth: '140px'}}>
                    <div className={'flex-grow-1'}>
                        <Editable onChange={(newValue) => onChange({ baseSpeed: newValue }, data.id)} value={data.baseSpeed} />
                    </div>
                    <div className={'pl-1 pr-1'}>
                        {translate('travian/tournament_square_short')}
                    </div>
                    <div className={'flex-grow-1'}>
                        <Editable onChange={(newValue) => onChange({ tournamentSquare: newValue }, data.id)} value={data.tournamentSquare} />
                    </div>
                    <div className={'ml-2'}>
                        <WithTooltip tooltip={translate('minitool/trav/speed_analysis/open_dialog')}>
                            <Button buttonTypes={[ButtonTypes.primary, ButtonTypes.small]} onClick={setModalOpen.bind(null, true)}>
                                <Icon type={'search'} />
                            </Button>
                        </WithTooltip>
                    </div>
                </div>
            </Column>
            <Column sizes={[1, null, 2]}>
                 <div className={'pr-2'}>
                     <Dropdown
                        initialValue={data.classification}
                        onChange={(newValue) => onChange({ classification: newValue }, data.id)}
                        buttonTypes={[ButtonTypes.small]}
                    >
                        <DropdownElement value={null} key={null}>{translate('tool/trav/22/classification_not_selected')}</DropdownElement>
                        {
                            BTIncomingAttackClassification.cases().map(
                                classification => <DropdownElement value={classification.value} key={classification.value}>
                                    {translate('tool/trav/22/classification_' + classification.value)}
                                </DropdownElement>
                            )
                        }
                    </Dropdown>
                 </div>
            </Column>
            <Column sizes={[1, null, 2]}>
                <div className={'pr-2 full-size'}>
                    <Editable onChange={(newValue) => onChange({ comment: newValue }, data.id)} value={data.comment} />
                </div>
            </Column>
            <Column sizes={[1, null, 2]}>
                <WithTooltip tooltip={translate('tool/trav/22/action_hide')}>
                    <Button buttonTypes={[ButtonTypes.primary, ButtonTypes.small]} onClick={() => onChange({ hide: true }, data.id)}>
                        <Icon type={'delete'} />
                    </Button>
                </WithTooltip>
                {
                    groupId === null ? null :
                    <WithTooltip tooltip={translate('tool/trav/22/action_create_deffcall')}>
                        <LinkButton href={`/group/${groupId}/deffcall/create?defenderDid=${data.defenderDid}`} buttonTypes={[ButtonTypes.primary, ButtonTypes.small]}>
                            <Icon type={'plus'}/>
                        </LinkButton>
                    </WithTooltip>
                }
            </Column>
        </ColumnGrid>
    </>
}
